import { JSONSchemaDefinition } from "@forgepoint/shared";

/**
 * Determines if a field should render inline (label left, input right).
 * Complex fields like objects, arrays, textareas, and union types render as block layout.
 * @param schema - The JSON schema definition for the field
 * @returns True if the field should render inline
 */
export function isInlineField(schema: JSONSchemaDefinition): boolean {
  if (typeof schema === "boolean") {
    return false;
  }
  const type = Array.isArray(schema.type) ? schema.type[0] : schema.type;
  if (type === "object" || type === "array") {
    return false;
  }
  if (schema.anyOf || schema.oneOf) {
    return false;
  }
  if (schema.format === "fpTextarea") {
    return false;
  }
  if (schema.isCronTimer) {
    return false;
  }
  if (
    type === "string" &&
    schema.maxLength !== undefined &&
    schema.maxLength > 100
  ) {
    return false;
  }
  return true;
}

/**
 * Whether a property is a section container (object/array) — these render
 * flush as headers, while leaf fields sit in the numbered gutter.
 * @param schema - The JSON schema definition for the field
 * @returns True if the field is an object or array container
 */
export function isSectionContainer(schema: JSONSchemaDefinition): boolean {
  if (typeof schema === "boolean") {
    return false;
  }
  const type = Array.isArray(schema.type) ? schema.type[0] : schema.type;
  return type === "object" || type === "array";
}

/**
 * Whether a top-level property counts as a numberable input — i.e. an editable
 * leaf field. Read-only calculations, constants, and section containers
 * (objects/arrays) are skipped so the numbering tracks just the inputs.
 * @param schema - The JSON schema definition for the field
 * @returns True if the field is an editable leaf input
 */
export function isNumberableInput(schema: JSONSchemaDefinition): boolean {
  if (typeof schema === "boolean") {
    return false;
  }
  if (schema.format === "fpCalculation" || schema.const !== undefined) {
    return false;
  }
  const type = Array.isArray(schema.type) ? schema.type[0] : schema.type;
  return type !== "object" && type !== "array";
}
